import axios from "axios";
import { getCookie, setCookie } from "./cookie";
import CryptoJS from "crypto-js";
import { get } from "lodash";

let authState: "PENDING" | "SUCCESS" | "ERROR" = "PENDING";
let currentClientId = "";
let planData: Partial<{
  organizationId: string;
  plan: string;
  expiredAt: number;
  features: string[];
}> = {};

function getMainDomain(url: string) {
  let mainDomain = url.match(
    /(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:/\n]+)/im
  )?.[1];
  if (!mainDomain) {
    return url;
  }
  if (mainDomain.split(".").length > 2) {
    const parts = mainDomain.split(".");
    mainDomain = parts.slice(parts.length - 2).join(".");
  }

  return mainDomain;
}

const whiteListOrigin = ["127.0.0.1", "localhost"];
const MultipleYearsVersionClients: Array<{
  clidText: string;
  expiredAt: number;
  origins?: string[];
}> = [
  {
    // client id: dannevang (Mathias Dannevang mathias@dannevang.org)
    clidText: "yruFnUP9yM3OfLFA8+Vvgw==",
    expiredAt: 1822435200, // 2027-10-02
  },
  {
    // client id: meiro
    clidText: "w7+CgUmAoqiicL1M/+ljjw==",
    expiredAt: 1872288000, // 2029-05-01
  },
  {
    // client id: testbeacas
    clidText: "2r+Yh0TuyMDICLBB8uRugg==",
    expiredAt: 1735689600, // 2025-01-01
    origins: ["easyemail.pro"],
  },
];

const decrypt = (clientId: string, ciphertext: string) => {
  const bytes = CryptoJS.AES.decrypt(ciphertext, "easy_email_pro_" + clientId);
  const originalText = bytes.toString(CryptoJS.enc.Utf8);
  return JSON.parse(originalText);
};

const encrypt = (clientId: string) => {
  const key = CryptoJS.enc.Utf8.parse("easy-email-pro");
  const iv = CryptoJS.enc.Utf8.parse("easy-email-pro_multiple_years");

  // 加密模式和填充模式
  const mode = CryptoJS.mode.CFB;
  const padding = CryptoJS.pad.Pkcs7;

  // 要加密的数据
  const dataToEncrypt = clientId;

  // 加密
  const encrypted = CryptoJS.AES.encrypt(dataToEncrypt, key, {
    iv: iv,
    mode: mode,
    padding: padding,
  });

  // 输出加密后的结果
  return encrypted.toString();
};

const tokenStore = {
  getToken() {
    if (typeof document === "undefined") return;
    return getCookie("e_e_p");
  },
  setToken(token: string) {
    if (typeof document === "undefined") return;
    return setCookie("e_e_p", token, 3600);
  },
  clearToken() {
    if (typeof document === "undefined") return;
    return setCookie("e_e_p", "", -1);
  },
};

const isEnterPrise = (clientId: string) => {
  const matchClient = MultipleYearsVersionClients.find(
    (item) => item.clidText === encrypt(clientId)
  );

  if (matchClient) {
    if (matchClient.expiredAt > +new Date() / 1000) {
      if (matchClient.expiredAt - +new Date() / 1000 < 5184000) {
        console.log("-----------------------------------------");
        console.log("-----------------------------------------");
        console.log("-----------------------------------------");
        console.error(
          "Your Easy Email Pro subscription is about to expire. Please contact ch.mao@qq.com"
        );
        console.error(
          "Your Easy Email Pro subscription is about to expire. Please contact ch.mao@qq.com"
        );
        console.error(
          "Your Easy Email Pro subscription is about to expire. Please contact ch.mao@qq.com"
        );
        console.log("-----------------------------------------");
        console.log("-----------------------------------------");
        console.log("-----------------------------------------");
      }

      const isServer =
        typeof window === "undefined" || typeof window.location === "undefined";

      if (matchClient.origins && !isServer) {
        if (whiteListOrigin.includes(window.location.hostname)) {
          return true;
        }

        const mainDomain = getMainDomain(window.location.origin);
        if (matchClient.origins.includes(mainDomain)) {
          return true;
        }
        return false;
      }
      authState = "SUCCESS";
      return true;
    } else {
      console.error("Subscription error");
      return false;
    }
  }

  return false;
};

let authPromise: Promise<any>;

class EditorAuthCore {
  async auth(clientId: string) {
    currentClientId = clientId;

    const handleAuth = async () => {
      if (authState !== "PENDING") return;

      if (isEnterPrise(clientId)) {
        return true;
      }

      const cacheToken = tokenStore.getToken();
      if (cacheToken) {
        try {
          const parsedData = decrypt(clientId, cacheToken);
          if (parsedData.expiredAt > +new Date() / 1000) {
            authState = "SUCCESS";
            planData = parsedData;
          }
        } catch (error) {
          tokenStore.clearToken();
        }
      }

      const handleToken = async () => {
        try {
          const domain =
            clientId === "FREE"
              ? "https://api.beacas.com"
              : "https://api.easyemail.pro";
          const { data } = await axios(
            `${domain}/api/public/token?client_id=${clientId}&v=${process.env.EASY_EMAIL_PRO_VERSION}`,
            {
              method: "GET",
              withCredentials: false,
            }
          );
          const parsedData = decrypt(clientId, data.access_token);
          planData = parsedData;
          tokenStore.setToken(data.access_token);

          authState = "SUCCESS";
        } catch (error) {
          try {
            const { data } = await axios(
              `https://api-easyemail-pro.vercel.app/api/public/token?client_id=${clientId}&v=${process.env.EASY_EMAIL_PRO_VERSION}`,
              {
                method: "GET",
              }
            );
            const parsedData = decrypt(clientId, data.access_token);
            planData = parsedData;
            tokenStore.setToken(data.access_token);
            authState = "SUCCESS";
          } catch (error) {
            console.log("error", error);
            authState = "ERROR";
            throw error;
          }
        }
      };

      if (authState === "SUCCESS") {
        handleToken();
      } else {
        await handleToken();
      }
    };

    if (!authPromise) {
      authPromise = handleAuth();
    }
    await authPromise;
  }

  get clientId() {
    return currentClientId;
  }

  getAuthState() {
    return authState;
  }

  getFeatureEnabled(feature: string): boolean {
    if (isEnterPrise(currentClientId)) {
      return true;
    }
    return get(planData, "features", [] as string[]).includes(feature);
  }
}

const EditorAuth = Object.freeze(new EditorAuthCore());

export { EditorAuth };
